package ai.flowstorm.studio.ui.builder

import ai.flowstorm.common.id.Id
import ai.flowstorm.common.ui.*
import ai.flowstorm.studio.resources.space.BuildRequestResource
import ai.flowstorm.studio.ui.design.dialogue.OpenDialogueModal
import io.kvision.core.TextAlign
import io.kvision.core.onClickLaunch
import io.kvision.core.style
import io.kvision.html.ButtonStyle
import io.kvision.html.Span
import io.kvision.html.button
import io.kvision.i18n.I18n
import io.kvision.i18n.tr
import io.kvision.panel.SimplePanel
import io.kvision.panel.hPanel
import io.kvision.panel.simplePanel
import io.kvision.state.ObservableValue
import io.kvision.state.bind
import io.kvision.state.observableListOf
import io.kvision.tabulator.*
import io.kvision.toolbar.Toolbar
import io.kvision.utils.perc
import kotlinx.coroutines.MainScope

class BuildRequestEditor(private val resource: BuildRequestResource) : SimplePanel(),
    EntityComponent<BuildRequest> {
    override var entity: BuildRequest? = null
    private val mainScope = MainScope()

    val items = observableListOf<BuildItem>()
    val form = BuildRequestForm()

    val state = ObservableValue<BuildRequest?>(null)


    init {
        add(form)

        hPanel {
            padding = UiHelpers.spacing


            simplePanel {
                button(tr("Start")).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch { start() }
                }
                button(tr("Stop")).bind(state) {
                    disabled =
                        state.value?.state != RequestState.Pending && state.value?.state != RequestState.InProgress
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch { stop() }
                }
                button(tr("Reset All")).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped && state.value?.state != RequestState.Finished
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch { reset() }
                }
                button(tr("Reset Failed")).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped && state.value?.state != RequestState.Finished
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch { resetFailed() }
                }
            }
            simplePanel{
                textAlign = TextAlign.RIGHT
                style {
                    flexGrow = 1
                }
                button(tr("Add Dialogue")).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped && state.value?.state != RequestState.Finished
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch {
                        val dialogue = OpenDialogueModal().open()
                        entity!!.addDialogue(
                            BuildItem(
                                dialogue.id,
                                "${dialogue.dialogueName}/${dialogue.version}",
                                ItemState.NotStarted
                            )
                        )
                        load(entity!!)
                    }
                }
                button(tr("Add Tree")).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped && state.value?.state != RequestState.Finished
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch {
                        val dialogue = OpenDialogueModal().open()
                        entity!!.addAllDialogues(BuildRequestResource.getTreeBuildRequest(dialogue).dialogues)
                        load(entity!!)
                    }
                }
                button(tr("Clear")).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped && state.value?.state != RequestState.Finished
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch {
                        entity!!.clearDialogues()
                        load(entity!!)
                    }
                }
                button(tr("Save")).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch { save() }
                }
                button(tr("Delete"), style = ButtonStyle.DANGER).bind(state) {
                    disabled = state.value?.state != RequestState.Stopped && state.value?.state != RequestState.Finished
                    marginRight = UiHelpers.spacing
                }.apply {
                    onClickLaunch { delete() }
                }
            }

        }
        tabulator(
            data = items, options = TabulatorOptions(
                layout = Layout.FITCOLUMNS, columns = listOf(
                    ColumnDefinition(
                        "Id",
                        "dialogue_id",
                        formatterComponentFunction = { _, _, data: BuildItem -> Span(data.dialogue_id.value) }),
                    ColumnDefinition("Name", "dialogueName"),
                    ColumnDefinition("Build state", "state"),
                    ColumnDefinition("", headerSort = false, formatterComponentFunction = { cell, _, data: BuildItem ->
                        Toolbar().apply {
                            add(
                                ToolbarButton(
                                    Icon.Delete,
                                    "remove",
                                    disabled = entity?.state != RequestState.Stopped
                                ).onClick {

                                    entity!!.dialogues.remove(data)
                                    updateItems()
                                    cell.getRow

                                    console.log("remove ", data.dialogue_id)
                                })
                            add(
                                ToolbarButton(
                                    Icon.External,
                                    "logs",
                                    disabled = data.state == ItemState.NotStarted || data.state == ItemState.Building
                                ).onClick {
                                    BuildResultModal().open(data)
                                })
                        }

                    }),
                )
            )
        ) {
            height = 100.perc
        }
    }

    private suspend fun save() {
        val formData = form.getData()

        entity = entity!!.copy(name = formData.name, description = formData.description)

        val saved = if (entity!!.isNew) resource.create(entity!!) else resource.update(entity!!)
        load(saved)
    }

    private suspend fun delete() {
        if (ConfirmModal.Delete().open()) resource.delete(entity!!)
    }

    private suspend fun stop() {
        if (ConfirmModal(
                I18n.tr("Confirmation"),
                I18n.tr("Do you really want to stop the build?")
            ).open()
        ) load(resource.stop(entity!!))
    }

    private suspend fun start() {
        if (ConfirmModal(
                I18n.tr("Confirmation"),
                I18n.tr("Do you really want to start the build?")
            ).open()
        ) load(resource.start(entity!!))
    }

    private suspend fun reset() {
        if (ConfirmModal(
                I18n.tr("Confirmation"),
                I18n.tr("Do you really want to reset the build results?")
            ).open()
        ) load(resource.reset(entity!!))
    }

    private suspend fun resetFailed() {
        if (ConfirmModal(
                I18n.tr("Confirmation"),
                I18n.tr("Do you really want to reset the results of failed builds?")
            ).open()
        ) load(resource.resetFailed(entity!!))
    }

    override fun load(id: Id): BuildRequest {
        val buildRequest = resource.get(id)

        return load(buildRequest)
    }

    private fun updateItems() {
        items.apply {
            clear()
            addAll(entity!!.dialogues)
        }
    }

    override fun load(entity: BuildRequest): BuildRequest {
        this.entity = entity
        form.load(entity)
        updateItems()

        state.value = entity

        return entity
    }

}
